/**
 * DunningBanner — the Manage Subscription primary banner during a Stripe
 * dunning (failed-payment retry) cycle, plus the paused + resolved states
 * that bracket it. Recreated from the design handoff's
 * ``rc_dunning_banner.jsx``.
 *
 * ONE component, parameterized by stage. Stage is keyed to the
 * ``billing_state.dunning_stage`` derived field (engineering wires the
 * Stripe webhook → field mapping; the strings here are fixed by design).
 *
 * Tone: calm, factual. "pause" never "cancel"; "Explore mode" never
 * "downgrade"; never "declined"/"delinquent". Data is always safe.
 *
 * NON-NEGOTIABLE: danger red appears on EXACTLY ONE control in this whole
 * surface — ``dunning_final``'s [Update card]. Every earlier stage uses the
 * regular ``sun`` primary.
 *
 * Mount at the top of ``ManageSubscriptionModal``, above the current-plan
 * card. Returns ``null`` when there's nothing to say (``stage`` falsy /
 * ``"none"``).
 */
import { Icon, type IconName } from "../chrome/Icon";
import { ChromeTokens } from "../chrome/tokens";
import type { DunningStage } from "./types";

interface DunningBannerProps {
  stage?: DunningStage | "none" | null;
  /** Human-readable due date for ``dunning_mid``/``dunning_final`` copy. */
  dueDate?: string | null;
  onUpdateCard: () => void;
  onResume: () => void;
  /** ``dunning_first`` only. */
  onDismiss: () => void;
  /** Render the one-line success line after a successful card update. */
  resolved?: boolean;
}

type Tint = "sun" | "coral" | "coral-strong" | "lavender";

interface StageConfig {
  tint: Tint;
  icon: IconName;
  title: string;
  body: string;
  cta: "primary" | "danger";
  ctaLabel: string;
  dismissible: boolean;
}

function stageConfig(stage: DunningStage, dueDate: string | null | undefined): StageConfig {
  const due = dueDate || "soon";
  switch (stage) {
    case "dunning_first":
      return {
        tint: "sun",
        icon: "credit-card",
        title: "We couldn't charge your card",
        body: "We'll try again automatically — or you can update your card now.",
        cta: "primary",
        ctaLabel: "Update card",
        dismissible: true,
      };
    case "dunning_mid":
      return {
        tint: "coral",
        icon: "calendar-clock",
        title: "Heads up — payment didn't go through",
        body: `Your subscription will pause if we can't process payment by ${due}.`,
        cta: "primary",
        ctaLabel: "Update card",
        dismissible: false,
      };
    case "dunning_final":
      return {
        tint: "coral-strong",
        icon: "alert-triangle",
        title: "Your subscription pauses tomorrow",
        body: "Your kids' projects stay safe — they'll just be in Explore mode until you resume.",
        cta: "danger",
        ctaLabel: "Update card",
        dismissible: false,
      };
    case "paused":
      return {
        tint: "lavender",
        icon: "sparkles",
        title: "Your subscription is paused",
        body: "Your kids' worlds are all safe in Explore mode. Pick up right where you left off whenever you're ready.",
        cta: "primary",
        ctaLabel: "Resume my plan",
        dismissible: false,
      };
  }
}

const BANNER_BG: Record<Tint | "grass", string> = {
  sun: `color-mix(in srgb, var(--chrome-color-sun) 30%, var(--chrome-color-base))`,
  coral: `color-mix(in srgb, var(--chrome-color-coral) 28%, var(--chrome-color-base))`,
  "coral-strong": `color-mix(in srgb, var(--chrome-color-coral) 42%, var(--chrome-color-base))`,
  lavender: `color-mix(in srgb, var(--chrome-color-lavender) 30%, var(--chrome-color-base))`,
  grass: `color-mix(in srgb, var(--chrome-color-grass) 30%, var(--chrome-color-base))`,
};

export function DunningBanner({
  stage,
  dueDate,
  onUpdateCard,
  onResume,
  onDismiss,
  resolved,
}: DunningBannerProps): JSX.Element | null {
  // After a successful card update from any dunning state: one calm line, no CTA.
  if (resolved) {
    return (
      <div
        role="status"
        style={{
          display: "flex",
          alignItems: "center",
          gap: 11,
          padding: "13px 15px",
          borderRadius: ChromeTokens.radius.md,
          background: BANNER_BG.grass,
        }}
      >
        <span
          style={{
            width: 28,
            height: 28,
            borderRadius: ChromeTokens.radius.pill,
            background: ChromeTokens.color.base,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
          }}
        >
          <Icon name="check" size={16} stroke="#3a6e3f" />
        </span>
        <div style={{ fontSize: 14, fontWeight: 800 }}>You're all set — thanks!</div>
      </div>
    );
  }

  if (!stage || stage === "none") return null;
  const cfg = stageConfig(stage, dueDate);
  const isResume = stage === "paused";

  const ctaBackground = cfg.cta === "danger" ? ChromeTokens.color.danger : ChromeTokens.color.sun;
  const ctaColor = cfg.cta === "danger" ? ChromeTokens.color.base : ChromeTokens.color.ink;

  return (
    <div
      role="alert"
      style={{
        position: "relative",
        padding: "15px 16px 16px",
        borderRadius: ChromeTokens.radius.md,
        background: BANNER_BG[cfg.tint],
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: ChromeTokens.spacing.md }}>
        <span
          style={{
            width: 34,
            height: 34,
            borderRadius: ChromeTokens.radius.pill,
            background: ChromeTokens.color.base,
            display: "grid",
            placeItems: "center",
            flexShrink: 0,
            marginTop: 1,
          }}
        >
          <Icon name={cfg.icon} size={18} stroke={ChromeTokens.color.clay} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 800,
              lineHeight: 1.25,
              paddingRight: cfg.dismissible ? 24 : 0,
            }}
          >
            {cfg.title}
          </div>
          <p style={{ fontSize: 13, color: ChromeTokens.color.ink, lineHeight: 1.5, margin: "4px 0 0", opacity: 0.85 }}>
            {cfg.body}
          </p>
        </div>
        {cfg.dismissible && (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Dismiss"
            style={{
              position: "absolute",
              top: 11,
              right: 11,
              width: 28,
              height: 28,
              borderRadius: ChromeTokens.radius.pill,
              border: "none",
              cursor: "pointer",
              background: `color-mix(in srgb, var(--chrome-color-ink) 7%, transparent)`,
              color: ChromeTokens.color.inkMuted,
              display: "grid",
              placeItems: "center",
            }}
          >
            <Icon name="x" size={15} />
          </button>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: ChromeTokens.spacing.sm + 2, marginTop: ChromeTokens.spacing.md, paddingLeft: 46 }}>
        <button
          type="button"
          onClick={isResume ? onResume : onUpdateCard}
          style={{
            minHeight: 40,
            padding: "0 16px",
            borderRadius: ChromeTokens.radius.pill,
            border: "none",
            cursor: "pointer",
            background: ctaBackground,
            color: ctaColor,
            fontFamily: ChromeTokens.font.ui,
            fontWeight: 800,
            fontSize: 14,
            boxShadow: ChromeTokens.shadow.soft,
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
          }}
        >
          <Icon name={isResume ? "rotate-ccw" : "credit-card"} size={15} stroke={ctaColor} />
          {cfg.ctaLabel}
        </button>
        {cfg.dismissible && (
          <button
            type="button"
            onClick={onDismiss}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: ChromeTokens.color.inkMuted,
              fontWeight: 700,
              fontSize: 13,
              minHeight: 40,
              padding: "0 6px",
            }}
          >
            Not now
          </button>
        )}
      </div>
    </div>
  );
}

export default DunningBanner;
